/**
 * Visibility scope -> Firestore query constraints (ARCHITECTURE.md "Visibility").
 * Must mirror firestore.rules: a query that asks for more than the scope allows
 * is rejected by the rules, so always start queries with these constraints.
 *
 * Unit scopes (General Managers, "Can see" units; functions/src/units.ts):
 * every scoped doc carries unitIds and the viewer's profile scopeUnitIds
 * (copied onto the app-side claims by lib/auth); a unit-scope query is
 * `unitIds array-contains-any scopeUnitIds`, matching the rules' hasAny().
 *
 *   query(collection(db, 'customers'), ...customerScopeConstraints(claims, uid), orderBy('lastVisitAt', 'desc'))
 *   query(collection(db, 'visits'), ...eventScopeConstraints(claims, uid, 'byUid'), orderBy('createdAt', 'desc'))
 *   query(collection(db, 'loginEvents'), ...eventScopeConstraints(claims, uid, 'uid'), orderBy('at', 'desc'))
 *   query(collection(db, 'leads'), ...leadScopeConstraints(claims, uid), orderBy('createdAt', 'desc'))
 *   query(collectionGroup(db, 'activities'), ...teamScopeConstraints(claims)!, orderBy('createdAt', 'desc'))   // My Team's
 *
 * Custom roles can widen a read to the whole collection (firestore.rules can()):
 * customers.viewAll (customers), visits.viewAll (visits), locations.view
 * (loginEvents), employees.view (users), leads.view (leads). The viewer's permissions ride on the
 * app-side claims (claims.permissions, set by lib/auth), and collectionScope()
 * is then 'all' for that collection, so the query drops its scope constraint
 * exactly where the rules allow it. Cache keys must use scopeKey().
 */
import { documentId, or, where, type QueryConstraint, type QueryFilterConstraint } from "firebase/firestore";
import { hasPermission } from "./permissions";
import { placementUnitIds, sharesUnit } from "./units";
import type { CrmClaims, Customer, Lead, PermissionMap, ScopeLevel, Visit } from "./types";

type UnitScope = Extract<ScopeLevel, "company" | "administration" | "department">;

/** Field on customers holding the ids for each unit scope. */
export const CUSTOMER_SCOPE_FIELDS: Record<UnitScope, "companyIds" | "administrationIds" | "departmentIds"> = {
  company: "companyIds",
  administration: "administrationIds",
  department: "departmentIds",
};

/** Field on visits / loginEvents holding the unit id for each unit scope. */
export const EVENT_SCOPE_FIELDS: Record<UnitScope, "companyId" | "administrationId" | "departmentId"> = {
  company: "companyId",
  administration: "administrationId",
  department: "departmentId",
};

/**
 * The effective scope: a unit scope needs a scopeId and at least one unit in
 * scopeUnitIds (from the profile), else it degrades to 'self'.
 */
export function effectiveScope(claims: CrmClaims | null): ScopeLevel {
  if (!claims) return "self";
  if (claims.scope === "all") return claims.role === "admin" ? "all" : "self";
  if (claims.scope === "self") return "self";
  return claims.scopeId && scopeUnitIdsOf(claims).length ? claims.scope : "self";
}

/** The units the viewer may read beyond their own records ([] = none). */
export function scopeUnitIdsOf(claims: CrmClaims | null): string[] {
  return claims?.scopeUnitIds ?? [];
}

/** The scoped collections (Visibility table). */
export type ScopedCollection = "customers" | "visits" | "loginEvents" | "users" | "leads";

const SCOPED_COLLECTIONS: readonly ScopedCollection[] = ["customers", "visits", "loginEvents", "users", "leads"];

/** The role permission that lifts the scope of each collection. */
function grantsReadAll(map: PermissionMap | undefined, collection: ScopedCollection): boolean {
  switch (collection) {
    case "customers":
      return hasPermission(map, "customers", "viewAll");
    case "visits":
      return hasPermission(map, "visits", "viewAll");
    case "loginEvents":
      return hasPermission(map, "locations", "view");
    case "users":
      return hasPermission(map, "employees", "view");
    case "leads":
      return hasPermission(map, "leads", "view");
  }
}

/** True when the viewer may read every document of `collection` (Super Admin, or the role permission above). */
export function readsAll(claims: CrmClaims | null, collection: ScopedCollection): boolean {
  if (effectiveScope(claims) === "all") return true;
  return !!claims?.active && grantsReadAll(claims.permissions, collection);
}

/** effectiveScope(), widened to 'all' for a collection the viewer reads in full. */
export function collectionScope(claims: CrmClaims | null, collection: ScopedCollection): ScopeLevel {
  return readsAll(claims, collection) ? "all" : effectiveScope(claims);
}

/** A cache key that changes whenever any query shape above would change. */
export function scopeKey(claims: CrmClaims | null, uid: string | null | undefined): string {
  const wide = SCOPED_COLLECTIONS.filter((c) => readsAll(claims, c)).join(",");
  return `${uid ?? ""}|${effectiveScope(claims)}|${scopeUnitIdsOf(claims).join(",")}|${wide}`;
}

/** Constraints for the customers collection. */
export function customerScopeConstraints(claims: CrmClaims | null, uid: string): QueryConstraint[] {
  const scope = collectionScope(claims, "customers");
  if (scope === "all") return [];
  // A plain employee: the clients he made, visited or has a task / deal on (firestore.rules canReadCustomer).
  if (scope === "self") return [where("linkedUids", "array-contains", uid)];
  return [where("unitIds", "array-contains-any", scopeUnitIdsOf(claims))];
}

/**
 * "My team's" work (functions/src/work-units.ts, firestore.rules
 * teamReadable): account activities, opportunities and lead activities carry
 * ownerUnitIds (their owner's and creator's units + uids), so a viewer with a
 * unit scope (a General Manager, or "Can see" units) reads the work of their
 * branch's people wherever the account sits. null when the viewer has no unit
 * scope (no team query to make).
 *
 *   query(collectionGroup(db, 'activities'), ...teamScopeConstraints(claims)!, orderBy('createdAt', 'desc'))
 */
export function teamScopeConstraints(claims: CrmClaims | null): QueryConstraint[] | null {
  const ids = claims?.active ? scopeUnitIdsOf(claims) : [];
  return ids.length ? [where("ownerUnitIds", "array-contains-any", ids)] : null;
}

/** May the viewer use the "My Team's" views (a unit scope)? */
export function hasTeamScope(claims: CrmClaims | null): boolean {
  return teamScopeConstraints(claims) !== null;
}

// ---------------------------------------------------------------------------
// Own work only (ARCHITECTURE.md "Own work only")
// ---------------------------------------------------------------------------

/** The account fields the own-work checks read (a customer, or accountOfCopies of a related record). */
type AccountAccess = Pick<Customer, "visitorIds" | "companyIds" | "administrationIds" | "departmentIds" | "unitIds" | "homeUnitIds"> & {
  createdBy?: string | null;
  linkedUids?: string[];
};

/**
 * Is the viewer linked to the account (firestore.rules canReadCustomer): a
 * visitor, or in its linkedUids (its owner, a visitor, or someone with a
 * task / deal on it)?
 */
export function linkedToAccount(uid: string, account: { visitorIds?: string[]; linkedUids?: string[] }): boolean {
  return !!uid && ((account.visitorIds ?? []).includes(uid) || (account.linkedUids ?? []).includes(uid));
}

/**
 * An account's home branch (firestore.rules homeUnits): homeUnitIds (its
 * Account Owner's units + uid), else its units. A related record's copied
 * unitIds already are its account's home units (accountOfCopies).
 */
export function homeUnitsOf(account: Pick<Customer, "companyIds" | "administrationIds" | "departmentIds" | "unitIds" | "homeUnitIds">): string[] {
  return account.homeUnitIds ?? customerUnitIds(account);
}

/** Is an item (contact, opportunity, activity, file) owned or created by someone of a branch the viewer heads (rules teamManaged)? */
function teamManaged(claims: CrmClaims, item: { ownerUnitIds?: string[] | null }): boolean {
  return sharesUnit(claims.gmUnitIds, item.ownerUnitIds ?? []);
}

/**
 * Does the viewer read every salesman's work on this account (its contacts,
 * opportunities, activities, files, history: firestore.rules
 * readsAccountWork)? Super Admins, customers.viewAll holders and the scope
 * (General Managers, "Can see" units) of its HOME branch. A plain employee
 * who visited it reads the account but only his own work on it; a General
 * Manager of another branch only his team's.
 */
export function readsAccountWork(
  claims: CrmClaims | null,
  account: Pick<Customer, "companyIds" | "administrationIds" | "departmentIds" | "unitIds" | "homeUnitIds">,
): boolean {
  if (!claims?.active) return false;
  if (readsAll(claims, "customers")) return true;
  return sharesUnit(scopeUnitIdsOf(claims), homeUnitsOf(account));
}

/** Is the viewer the account's owner (customers.createdBy; firestore.rules ownsAccount)? */
export function ownsAccount(uid: string, account: { createdBy?: string | null }): boolean {
  return !!uid && account.createdBy === uid;
}

/** Is a work item (opportunity, activity) the viewer's own: they own it or created it (firestore.rules ownsWork)? */
export function ownsWork(uid: string, item: { ownerUid?: string | null; createdBy?: string | null }): boolean {
  return !!uid && (item.ownerUid === uid || item.createdBy === uid);
}

/**
 * Who manages every salesman's work on an account (edits anyone's contact,
 * opportunity or activity there; firestore.rules managesAccountWork): Super
 * Admins, the General Managers of its home branch, customers.update holders
 * who can read it.
 */
export function managesAccountWork(claims: CrmClaims | null, uid: string, account: AccountAccess): boolean {
  if (!claims?.active || !uid) return false;
  if (claims.role === "admin") return true;
  return sharesUnit(claims.gmUnitIds, homeUnitsOf(account)) || canFullyEditCustomer(claims, uid, account);
}

/** May the viewer edit this opportunity / activity: their own, their people's (GM), or one they manage (firestore.rules)? */
export function canEditWorkItem(
  claims: CrmClaims | null,
  uid: string,
  item: { ownerUid?: string | null; createdBy?: string | null; ownerUnitIds?: string[] | null },
  account: AccountAccess,
): boolean {
  if (!claims?.active || !uid) return false;
  return ownsWork(uid, item) || teamManaged(claims, item) || managesAccountWork(claims, uid, account);
}

/**
 * May the viewer give this activity to someone else (firestore.rules
 * activities update, reassigned): its creator, the General Managers of its
 * people (teamManaged) or of the account (managesAccountWork), Super Admins.
 * Its assignee edits and closes it but does not reassign it.
 */
export function canReassignWorkItem(
  claims: CrmClaims | null,
  uid: string,
  item: { createdBy?: string | null; ownerUnitIds?: string[] | null },
  account: AccountAccess,
): boolean {
  if (!claims?.active || !uid) return false;
  return item.createdBy === uid || teamManaged(claims, item) || managesAccountWork(claims, uid, account);
}

/**
 * May the viewer edit this contact: its creator, his General Managers, or a
 * manager of the account's work? Not the Account Owner as such: another
 * salesman's contact on his account is that salesman's work.
 */
export function canEditContact(
  claims: CrmClaims | null,
  uid: string,
  contact: { createdBy?: string | null; ownerUnitIds?: string[] | null },
  account: AccountAccess,
): boolean {
  if (!claims?.active || !uid) return false;
  return contact.createdBy === uid || teamManaged(claims, contact) || managesAccountWork(claims, uid, account);
}

/**
 * The scope filter of a collection-group read of accounts' related records
 * (the Contacts / Opportunities / Tasks pages, the Calendar), on the copies
 * of the account the functions keep on each record (firestore.rules
 * relatedReadable / ownsWork): null = no filter (Super Admins,
 * customers.viewAll); a unit scope reads its units' records
 * (`unitIds array-contains-any scopeUnitIds`); everyone else only their own
 * (ownWorkFilter), never the other visitors' records on a shared account.
 *
 *   const f = relatedScopeFilter(claims, uid, "work")
 *   query(collectionGroup(db, "activities"), ...(f ? [and(f, where("dueDate", ">=", from))] : [where(...)]), orderBy("dueDate"))
 */
export function relatedScopeFilter(claims: CrmClaims | null, uid: string, kind: "contacts" | "work"): QueryFilterConstraint | null {
  const scope = collectionScope(claims, "customers");
  if (scope === "all") return null;
  if (scope !== "self") return where("unitIds", "array-contains-any", scopeUnitIdsOf(claims));
  return ownWorkFilter(uid, kind);
}

/**
 * "My own" related records: contacts I created; opportunities / activities I
 * own or created (an OR query, each side allowed by the rules' ownsWork).
 * Also the narrowed read of one account whose whole lists the viewer may
 * not read (lib/data/related.ts subscribeList).
 */
export function ownWorkFilter(uid: string, kind: "contacts" | "work"): QueryFilterConstraint {
  return kind === "contacts" ? where("createdBy", "==", uid) : or(where("ownerUid", "==", uid), where("createdBy", "==", uid));
}

/**
 * Does the viewer read this account in full (firestore.rules canReadCustomer:
 * Super Admin, customers.viewAll, a visitor, or one of its units in scope)?
 * Otherwise they reach it only because their people work on it
 * (customers.workUnitIds): read only, its details and contacts, and of its
 * activities / opportunities only their team's.
 */
export function readsAccountFully(
  claims: CrmClaims | null,
  uid: string,
  account: Pick<Customer, "visitorIds" | "companyIds" | "administrationIds" | "departmentIds" | "unitIds" | "linkedUids">,
): boolean {
  if (!claims?.active || !uid) return false;
  if (readsAll(claims, "customers")) return true;
  if (linkedToAccount(uid, account)) return true;
  return sharesUnit(scopeUnitIdsOf(claims), customerUnitIds(account));
}

/**
 * Constraints for the leads collection (firestore.rules leadReadable):
 * leads.view holders and Super Admins read every lead (no constraint, the
 * unassigned web queue included); unit scopes read `unitIds
 * array-contains-any scopeUnitIds` (the owner's units + the owner's uid);
 * everyone else reads the leads they own.
 *
 *   query(collection(db, 'leads'), ...leadScopeConstraints(claims, uid), orderBy('createdAt', 'desc'))
 */
export function leadScopeConstraints(claims: CrmClaims | null, uid: string): QueryConstraint[] {
  const scope = collectionScope(claims, "leads");
  if (scope === "all") return [];
  if (scope === "self") return [where("ownerUid", "==", uid)];
  return [where("unitIds", "array-contains-any", scopeUnitIdsOf(claims))];
}

/**
 * Constraints for visits (uidField 'byUid') and loginEvents (uidField 'uid').
 */
export function eventScopeConstraints(
  claims: CrmClaims | null,
  uid: string,
  uidField: "byUid" | "uid",
): QueryConstraint[] {
  const scope = collectionScope(claims, uidField === "byUid" ? "visits" : "loginEvents");
  if (scope === "all") return [];
  if (scope === "self") return [where(uidField, "==", uid)];
  return [where("unitIds", "array-contains-any", scopeUnitIdsOf(claims))];
}

/**
 * Constraints for the users collection (users carry companyId / administrationId /
 * departmentId). The 'self' scope reads only the caller's own profile.
 *
 *   query(collection(db, 'users'), ...userScopeConstraints(claims, uid))
 */
export function userScopeConstraints(claims: CrmClaims | null, uid: string): QueryConstraint[] {
  const scope = collectionScope(claims, "users");
  if (scope === "all") return [];
  if (scope === "self") return [where(documentId(), "==", uid)];
  return [where("unitIds", "array-contains-any", scopeUnitIdsOf(claims))];
}

// ---------------------------------------------------------------------------
// Edit permissions (mirror the rules functions of the same name)
// ---------------------------------------------------------------------------

/**
 * Who may edit a visit (firestore.rules canEditVisit): its author, every
 * General Manager whose unit contains the author (the same scope test as
 * reading it), holders of visits.update for every visit they can read, and
 * Super Admins. The caller must also be active and verified, which
 * RequireAuth already guarantees for the pages that show the sheet.
 */
export function canEditVisit(
  claims: CrmClaims | null,
  uid: string,
  visit: Pick<Visit, "byUid" | "companyId" | "administrationId" | "departmentId" | "unitIds">,
): boolean {
  if (!claims || !claims.active || !uid) return false;
  if (claims.role === "admin") return true;
  if (visit.byUid === uid) return true;
  if (hasPermission(claims.permissions, "visits", "update") && readsAll(claims, "visits")) return true;
  if (hasPermission(claims.permissions, "visits", "update") && sharesUnit(scopeUnitIdsOf(claims), visit.unitIds ?? placementUnitIds(visit))) return true;
  return sharesUnit(claims.gmUnitIds, visit.unitIds ?? placementUnitIds(visit));
}

/**
 * Who may edit a customer's details in the admin dashboard (firestore.rules
 * canFullyEditCustomer): Super Admins, and holders of customers.update for
 * every customer they can read (visited, in their GM unit, or all with
 * customers.viewAll).
 */
export function canFullyEditCustomer(
  claims: CrmClaims | null,
  uid: string,
  customer: Pick<Customer, "visitorIds" | "companyIds" | "administrationIds" | "departmentIds" | "unitIds" | "linkedUids">,
): boolean {
  if (!claims || !claims.active || !uid) return false;
  if (claims.role === "admin") return true;
  if (!hasPermission(claims.permissions, "customers", "update")) return false;
  if (linkedToAccount(uid, customer) || sharesUnit(scopeUnitIdsOf(claims), customerUnitIds(customer))) return true;
  return readsAll(claims, "customers");
}

/**
 * Who may edit an account's own fields (name, phone, address, type, rating,
 * ...) without a role permission (firestore.rules canEditAccountFields +
 * isCustomerFieldsUpdate): its Account Owner (who created it) and the
 * General Managers of its home branch. Its other visitors may not.
 * Full editors (canFullyEditCustomer) may too; callers add that themselves.
 */
export function canEditAccountFields(
  claims: CrmClaims | null,
  uid: string,
  customer: Pick<Customer, "companyIds" | "administrationIds" | "departmentIds" | "unitIds" | "homeUnitIds"> & { createdBy?: string | null },
): boolean {
  if (!claims || !claims.active || !uid) return false;
  if (ownsAccount(uid, customer)) return true;
  return sharesUnit(claims.gmUnitIds, homeUnitsOf(customer));
}

/** A customer's units (stored, else the three arrays of customers from before unitIds). */
export function customerUnitIds(customer: Pick<Customer, "companyIds" | "administrationIds" | "departmentIds" | "unitIds">): string[] {
  return customer.unitIds ?? [...(customer.companyIds ?? []), ...(customer.administrationIds ?? []), ...(customer.departmentIds ?? [])];
}

/**
 * Who may add contacts, opportunities, activities and files to an account
 * (firestore.rules canWriteRelated): Super Admins, its visitors, its Account
 * Owner, whoever has a task / deal on it (linkedUids), its General Managers, and customers.update holders. Editing an
 * existing one is canEditWorkItem / canEditContact (own work only).
 */
export function canWorkOnAccount(claims: CrmClaims | null, uid: string, account: AccountAccess): boolean {
  if (!claims?.active || !uid) return false;
  if (claims.role === "admin" || linkedToAccount(uid, account) || ownsAccount(uid, account)) return true;
  return sharesUnit(claims.gmUnitIds, customerUnitIds(account)) || canFullyEditCustomer(claims, uid, account);
}

// ---------------------------------------------------------------------------
// Leads (mirror firestore.rules leadReadable / canWorkLead)
// ---------------------------------------------------------------------------

type LeadAccess = Pick<Lead, "ownerUid" | "unitIds">;

/** Who may read a lead: Super Admins, its owner, its units' readers, leads.view holders. */
export function canReadLead(claims: CrmClaims | null, uid: string, lead: LeadAccess): boolean {
  if (!claims?.active || !uid) return false;
  if (claims.role === "admin") return true;
  if (lead.ownerUid === uid) return true;
  if (sharesUnit(scopeUnitIdsOf(claims), lead.unitIds ?? [])) return true;
  return hasPermission(claims.permissions, "leads", "view");
}

/**
 * Who may edit, assign or convert a lead (firestore.rules canWorkLead and the
 * assignLead / convertLead callables): Super Admins, its owner, every General
 * Manager whose unit holds it, and leads.update holders for a lead they can
 * see (leads.view, or one of their scope units). A converted lead is
 * read-only for everyone (check `lead.status !== 'converted'` too).
 */
export function canWorkLead(claims: CrmClaims | null, uid: string, lead: LeadAccess): boolean {
  if (!claims?.active || !uid) return false;
  if (claims.role === "admin") return true;
  if (lead.ownerUid === uid) return true;
  if (sharesUnit(claims.gmUnitIds, lead.unitIds ?? [])) return true;
  if (!hasPermission(claims.permissions, "leads", "update")) return false;
  return hasPermission(claims.permissions, "leads", "view") || sharesUnit(scopeUnitIdsOf(claims), lead.unitIds ?? []);
}

/** Who may delete a lead: Super Admins only. */
export function canDeleteLead(claims: CrmClaims | null): boolean {
  return !!claims?.active && claims.role === "admin";
}

/** Who may delete one of an account's related records: Super Admins, its creator, customers.update holders. */
export function canDeleteRelated(
  claims: CrmClaims | null,
  uid: string,
  account: Pick<Customer, "visitorIds" | "companyIds" | "administrationIds" | "departmentIds" | "unitIds"> | null,
  createdBy: string,
): boolean {
  if (!claims?.active || !uid) return false;
  return claims.role === "admin" || createdBy === uid || (!!account && canFullyEditCustomer(claims, uid, account));
}
